// show all ayurvedic doctors on public page

import { useState, useEffect } from "react";
import {
  Star,
  Leaf,
  Users,
  Phone,
  MessageCircle,
  CheckCircle,
  Stethoscope,
} from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
// import { fetchAllAyurvedicDoctors } from "../../AyurvedicDoctor/ayurvedicDoctorAPIs";
import { fetchAyurvedicDoctors } from "./ayurvedicDoctorsPageApi";
import { SEO } from "../../SEO";

const fallbackDoctorImage = (name: string) =>
  `https://via.placeholder.com/400x300?text=${encodeURIComponent(name)}`;

const DoctorImagePreview = ({
  src,
  alt,
}: {
  src: string;
  alt: string;
}) => (
  <div className="mb-4 h-40 overflow-hidden rounded-lg bg-gray-700">
    <div className="relative h-full w-full overflow-hidden">
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-70"
      />
      <div className="absolute inset-0 bg-black/10" />
      <img
        src={src}
        alt={alt}
        className="relative z-10 h-full w-full object-contain"
        onError={(e) => {
          (e.target as HTMLImageElement).src = fallbackDoctorImage(alt);
        }}
      />
    </div>
  </div>
);

const DoctorImagePlaceholder = () => (
  <div className="mb-4 flex h-40 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-teal-900/40 via-gray-700 to-gray-800">
    <span className="px-4 text-center text-lg font-semibold tracking-wide text-teal-100">
      Ayurvedic Doctor
    </span>
  </div>
);

interface ApiInstructor {
  firstname: string;
  lastname: string;
  mentorId: string;
  bio?: string;
  experience?: string;
  languages?: string[];
  profileIcon?: string;
  profileQuote?: string;
  certification?: string[];
  specializations?: string | string[];
  socialLinks?: {
    demoVideo?: string;
  };
  Skills?: string[];
  skill?: string[];
  slug?: string;
  createAt: string;
}

interface AyurvedicDoctor {
  mentorId: string;
  name: string;
  specialty: string;
  experience: string;
  rating: number;
  languages: string[];
  image: string;
  description: string;
  availability: string;
  Skills: string[];
  consultationTypes: string[];
  slug: string;
}

const transformApiToDoctor = (api: ApiInstructor): AyurvedicDoctor => {
  // Handle specializations - can be string or array
  let specialty = "Ayurvedic Doctor";
  if (api.specializations) {
    specialty = Array.isArray(api.specializations)
      ? api.specializations.join(", ")
      : api.specializations;
  } else if (api.bio) {
    specialty = api.bio;
  }

  return {
    mentorId: api.mentorId || (api as any).doctorId,
    name: `${api.firstname} ${api.lastname}`,
    specialty,
    experience: api.experience || "New",
    rating: 4.5,
    languages: api.languages || ["English"],
    image: api.profileIcon || "",
    description:
      api.bio || "Experienced Ayurvedic doctor focused on holistic healing.",
    availability: "Available",
    Skills: api.Skills || api.skill || [],
    consultationTypes: api.skill || [],
    slug: api.slug || "",
  };
};

interface AyurvedicDoctorsApiResponse {
  data: ApiInstructor[];
}

const AyurvedicDoctorPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [allSkills, setAllSkills] = useState<string[]>([]);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);

  const [ayurvedicDoctors, setAyurvedicDoctors] = useState<AyurvedicDoctor[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState("General");

  const specialties = [
    "General",
    "Panchakarma",
    "Skin & Hair",
    "Digestive",
    "Joint & Pain",
    "Women's Health",
  ];

  // Fetch Ayurvedic Doctors from API
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoading(true);

        const res =
          (await fetchAyurvedicDoctors()) as AyurvedicDoctorsApiResponse;
        // console.log("Fetched Ayurvedic Doctors:", res);

        if (Array.isArray(res.data)) {
          setAyurvedicDoctors(res.data.map(transformApiToDoctor));
        } else {
          setAyurvedicDoctors([]);
        }
      } catch (err) {
        console.error("Failed to fetch doctors", err);
        setAyurvedicDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    loadDoctors();
  }, []);

  const filteredDoctors =
    selectedSpecialty === "General"
      ? ayurvedicDoctors
      : ayurvedicDoctors.filter((d) =>
          d.specialty.toLowerCase().includes(selectedSpecialty.toLowerCase())
        );

  const testimonials = [
    {
      name: "Sunita Joshi, 42",
      text: "After months of digestion issues, I finally feel healthy again. Ayurveda works!",
      rating: 5,
      condition: "Digestive Issues",
    },
    {
      name: "Ankita, 35",
      text: "Their doctor took the time to understand my lifestyle. I feel balanced and calm now.",
      rating: 5,
      condition: "Stress & Anxiety",
    },
    {
      name: "Nisha Verma, 35",
      text: "Metafit's Ayurvedic support helped me through post-COVID recovery naturally.",
      rating: 5,
      condition: "Post-COVID Recovery",
    },
    {
      name: "Rajat Mehra, 51",
      text: "Excellent consultation! The customized herbal routine made a big difference.",
      rating: 5,
      condition: "Joint Pain",
    },
  ];

  const whyAyurveda = [
    "Natural Healing: Treat root cause, not just symptoms.",
    "Customized Treatment: Diet, herbs & lifestyle as per your dosha.",
    "Holistic Wellness: Mind-body balance through proven methods.",
  ];

  const whatToExpect = [
    "Dosha Assessment: Vata, Pitta, Kapha analysis by experts.",
    "Herbal Remedies: Safe and effective traditional medicines.",
    "Diet & Lifestyle: Guidance tailored for long-term results.",
  ];

  const suitability = [
    "Chronic lifestyle disorders?",
    "Digestive issues or skin problems?",
    "Fatigue, stress, or hormonal imbalances?",
    "Looking for natural alternatives?",
    "Want a sustainable health plan?",
  ];

  const treatments = [
    {
      name: "Panchakarma Detox",
      description: "Complete body detoxification and rejuvenation",
      duration: "7-21 days",
      price: "₹15,000 - ₹45,000",
    },
    {
      name: "Herbal Consultations",
      description: "Personalized herbal medicine prescriptions",
      duration: "45-60 minutes",
      price: "₹800 - ₹1,600",
    },
    {
      name: "Lifestyle Counseling",
      description: "Dosha-based diet and lifestyle guidance",
      duration: "30-45 minutes",
      price: "₹600 - ₹1,200",
    },
    {
      name: "Specialized Therapies",
      description: "Targeted treatments for specific conditions",
      duration: "60-90 minutes",
      price: "₹1,200 - ₹2,500",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Expert Ayurvedic Doctors Consultation & Holistic Healing | MetaFit Wellness"
        description="Book personalized consultations with certified Ayurvedic doctors at MetaFit Wellness. Balance your doshas with authentic herbal remedies, diet plans, and holistic treatments."
        canonical="/ayurvedic-doctor"
        keywords="ayurvedic doctor, ayurveda consultation, dosha assessment, panchakarma, herbal remedies, holistic health, MetaFit Wellness"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Personalized Ayurvedic Healing
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
              At Metafit Wellness, our expert Ayurvedic doctors offer
              personalized consultation rooted in ancient science—bringing
              balance to your doshas and restoring natural harmony in your body
              and mind.
            </p>
          </div>
          <div className="text-center">
            <Link to="/Ayurvedic-doctors-classes">
              <button
                // onClick={handleTryFreeSession}
                className="bg-yellow-300 hover:bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Get Consultations
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Information Cards Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-green-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Leaf className="w-6 h-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Why Ayurveda?
                </h3>
              </div>
              <ul className="space-y-2">
                {whyAyurveda.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-start text-sm text-gray-700"
                  >
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Stethoscope className="w-6 h-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  What to Expect
                </h3>
              </div>
              <ul className="space-y-2">
                {whatToExpect.map((expectation, index) => (
                  <li
                    key={index}
                    className="flex items-start text-sm text-gray-700"
                  >
                    <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    {expectation}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-orange-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Is This for You?
                </h3>
              </div>
              <ul className="space-y-2">
                {suitability.map((point, index) => (
                  <li
                    key={index}
                    className="flex items-start text-sm text-gray-700"
                  >
                    <CheckCircle className="w-4 h-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Doctors Section */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Meet Our Expert Ayurvedic Doctors
          </h2>

          {/* Specialty Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {specialties.map((s) => (
              <button
                key={s}
                onClick={() => setSelectedSpecialty(s)}
                className={`px-4 py-2 rounded-lg ${
                  selectedSpecialty === s
                    ? "bg-teal-600 text-white"
                    : "bg-gray-700 text-gray-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-gray-300">
              Loading Ayurvedic doctors...
            </p>
          ) : filteredDoctors.length === 0 ? (
            <p className="text-center text-gray-300">
              No doctors available at the moment
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor) => (
                <Link
                  key={doctor.mentorId}
                  to={`/ayurvedic-doctor/${doctor.mentorId}`}
                  className="h-full"
                >
                  <div className="flex h-full flex-col rounded-lg border border-gray-700 bg-gray-800 p-6 transition-all hover:border-teal-500 hover:shadow-lg hover:shadow-teal-500/20">
                    {/* Profile Photo */}
                    {doctor.image && (
                      <DoctorImagePreview
                        src={doctor.image}
                        alt={doctor.name}
                      />
                    )}
                    {!doctor.image && <DoctorImagePlaceholder />}

                    <h3 className="text-lg font-semibold text-white">
                      {doctor.name}
                    </h3>

                    {doctor.specialty && (
                      <p className="text-teal-400 text-sm mt-1 line-clamp-2">
                        {doctor.specialty}
                      </p>
                    )}

                    <div className="mt-3 text-sm text-gray-300 space-y-1">
                      <p>Experience: {doctor.experience}</p>
                      <p>Languages: {doctor.languages.join(", ")}</p>
                    </div>

                    <div className="flex items-center mt-3">
                      <Star className="w-4 h-4 text-yellow-400 mr-1" />
                      <span className="text-white">{doctor.rating}</span>
                    </div>

                    {doctor.description && (
                      <p className="mt-3 min-h-[3.5rem] text-sm text-gray-400 line-clamp-2">
                        {doctor.description}
                      </p>
                    )}
                    {!doctor.description && <div className="mt-3 min-h-[3.5rem]" />}

                    {/* Consultation Types */}
                    {doctor.consultationTypes && doctor.consultationTypes.length > 0 && (
                      <div className="mt-auto border-t border-gray-700 pt-4">
                        <p className="text-xs font-semibold text-gray-300 mb-2">
                          Types of Consultations:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {doctor.consultationTypes.slice(0, 3).map((type, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-teal-900 text-teal-300 text-xs rounded-full line-clamp-1"
                            >
                              {type}
                            </span>
                          ))}
                          {doctor.consultationTypes.length > 3 && (
                            <span className="px-2 py-1 bg-teal-900 text-teal-300 text-xs rounded-full">
                              +{doctor.consultationTypes.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {(!doctor.consultationTypes ||
                      doctor.consultationTypes.length === 0) && (
                      <div className="mt-auto border-t border-gray-700 pt-4 text-xs font-semibold text-gray-500">
                        No consultation types listed
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Patient Testimonials */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Patients Speak
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 text-sm mb-3 italic">
                  "{testimonial.text}"
                </p>
                <div className="border-t pt-3">
                  <p className="font-medium text-gray-900">
                    — {testimonial.name}
                  </p>
                  <p className="text-xs text-teal-600 mt-1">
                    {testimonial.condition}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-teal-600 to-green-600 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Begin Your Ayurvedic Healing Journey
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Connect with our experienced doctors for personalized treatment
            plans
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Book Consultation Now
            </button>
            <button className="flex items-center justify-center border border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-teal-600 transition-colors">
              <Phone className="w-5 h-5 mr-2" />
              Call for Free Guidance
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AyurvedicDoctorPage;
